import { AutocompletePredictionRestrictionTypes } from '../types';
import { CountryCode } from 'types/countries';
import GoogleMapsApiLoader from './loader';

function describeGoogleError(error: unknown): string {
  const message = error instanceof Error ? error.message : 'Unknown error';
  return message.replace(/key=[^&\s]+/gi, 'key=REDACTED');
}

// Google Places Autocomplete call layer. Market-specific searches live in
// residentialPlaces.ts, commercialPlaces.ts and homeAppraisalPlaces.ts.
// One request can mix up to 5 specific types and returns at most 5 results.
// https://developers.google.com/maps/documentation/javascript/legacy/supported_types

export async function getAutoCompleteService() {
  try {
    const { AutocompleteService } = await GoogleMapsApiLoader.importLibrary('places');
    return new AutocompleteService();
  } catch (error) {
    console.warn('Google Places library failed to load', describeGoogleError(error));
    return undefined;
  }
}

// No sessionToken. We never call Place Details, so a token would not close a
// session. Google would bill that as Autocomplete without Place Details.
// https://developers.google.com/maps/documentation/places/web-service/legacy/autocomplete#sessiontoken
export async function createAutocompletionRequest(
  input: string,
  type: string | readonly string[] = AutocompletePredictionRestrictionTypes.GEOCODE,
  country: readonly CountryCode[]
) {
  const request: google.maps.places.AutocompletionRequest = {
    input,
    componentRestrictions: { country: [... country]},
    types: typeof type === 'string' ? [type] : [...type],
  };
  return request;
}

export function assertGoogleApiKey() {
  if (!process.env.NEXT_PUBLIC_GOOGLE_API_KEY) {
    throw new Error('No Google API key was found.');
  }
}

export async function requireAutocompleteService() {
  assertGoogleApiKey();
  const autocompleteService = await getAutoCompleteService();
  if (!autocompleteService) {
    throw new Error('AutocompleteService could not be created.');
  }
  return autocompleteService;
}

// A hung Places call must not block the search dropdown or the map. Go-search can still render.
export const GOOGLE_PLACES_REQUEST_TIMEOUT_MS = 3000;

// One billed request to Google. A failure or timeout drops only these results.
// A late rejection after the timeout is swallowed so it cannot block the next Places call.
export async function requestPlacePredictions(
  autocompleteService: google.maps.places.AutocompleteService,
  request: google.maps.places.AutocompletionRequest,
): Promise<google.maps.places.AutocompletePrediction[]> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const placesPromise = autocompleteService.getPlacePredictions(request);
  void placesPromise.catch(() => undefined);
  try {
    const response = await Promise.race([
      placesPromise,
      new Promise<null>(resolve => {
        timeoutId = setTimeout(() => resolve(null), GOOGLE_PLACES_REQUEST_TIMEOUT_MS);
      }),
    ]);
    return response?.predictions ?? [];
  } catch (error) {
    console.warn('Google Places autocomplete request failed', describeGoogleError(error));
    return [];
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

export async function fetchPlacePredictions(
  autocompleteService: google.maps.places.AutocompleteService,
  input: string,
  types: string | readonly string[],
  country: readonly CountryCode[]
): Promise<google.maps.places.AutocompletePrediction[]> {
  const request = await createAutocompletionRequest(input, types, country);
  return requestPlacePredictions(autocompleteService, request);
}
